import { useMemo, useRef, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { AiPromptModal } from '@/components/commons/ai-prompt-modal'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, Settings01Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { ExpShellMerchInputForm } from '@/features/experience-vetting/components/exp-shell-merch-input-form'
import { ExpShellMerchOutputForm } from '@/features/experience-vetting/components/exp-shell-merch-output-form'
import type {
	ExperienceMerchandisingInputSchema,
	ExperienceShellMerchOutputSchema
} from '@/features/experience-vetting/form-schemas'
import { useMerchCopyAi } from '@/features/ops-vetting/hooks/use-merch-copy-ai'
import { EXPERIENCE_SHELL_PROMPT } from '@/features/ops-vetting/prompts/experience-shell-prompt'
import { useTranslation } from '@/translations/i18n'
import { useChangeStep } from '../hooks/use-change-step'
import { useExperienceShellFormContext } from '../hooks/use-experience-shell-form-context'
import { StepKeyName } from '../types'
import { Content, Footer } from './atoms'

export const MerchandisingStep = ({
	experienceId,
	isUpgrade
}: {
	experienceId: number
	isUpgrade: boolean
}) => {
	const { t } = useTranslation()
	const { getValues, setValue } = useExperienceShellFormContext(isUpgrade)
	const merchandisingData = useMemo(
		() => getValues('merchandising'),
		[getValues]
	)
	const { activities: _, ...rest } = merchandisingData
	const originalDefaultValues = { ...rest }
	const {
		aiResponse,
		isLoading: isAiLoading,
		onMerchCopyAiCall,
		resetAiResponse
	} = useMerchCopyAi<ExperienceShellMerchOutputSchema>()
	const improvedDefaultValues = useMemo(
		() => (aiResponse ? aiResponse : merchandisingData),
		[aiResponse, merchandisingData]
	)
	//prompt controls
	const [currentPrompt, setCurrentPrompt] = useState<string>(
		EXPERIENCE_SHELL_PROMPT
	)
	//submission refs
	const finalSubmitRef = useRef<HTMLButtonElement>(null)
	const aiSubmissionRef = useRef<HTMLButtonElement>(null)
	//trigger ai-improvement submission
	const handleAiSubmission: SubmitHandler<
		ExperienceMerchandisingInputSchema
	> = (data) => {
		resetAiResponse()
		onMerchCopyAiCall(data, currentPrompt)
	}

	//change step & edit experience
	const {
		editExperienceMutation,
		isBackLoading,
		isContinueLoading,
		handleChangeStep
	} = useChangeStep({
		experienceId,
		currentStep: StepKeyName.enum.merchandising,
		isUpgrade
	})

	const handleSaveOriginalMerchCopy = (
		data: ExperienceMerchandisingInputSchema
	) => {
		const dataToSave = { ...data }
		dataToSave.activities = undefined

		editExperienceMutation.mutate(dataToSave, {
			onSuccess: () => {
				setValue('merchandising', {
					...getValues('merchandising'),
					...dataToSave
				})
				toast.success(t('experience_shell:merchandising_save_success'))
			}
		})
	}

	const handleSaveAiMerchCopy = (data: ExperienceShellMerchOutputSchema) => {
		editExperienceMutation.mutate(
			{
				...data
			},
			{
				onSuccess: () => {
					setValue('merchandising', {
						...getValues('merchandising'),
						...data
					})
					toast.success(
						t('experience_shell:merchandising_save_success')
					)
				}
			}
		)
	}

	return (
		<>
			<Content
				fullWidth
				title={t('experience_shell:step_merchandising')}
				titleEndSlot={
					<AiPromptModal
						currentPrompt={currentPrompt}
						isLoading={isAiLoading}
						onPromptChange={(newPrompt) =>
							setCurrentPrompt(newPrompt)
						}
						promptType='exp_shell'
						trigger={
							<ButtonLegacy
								disabled={
									isAiLoading ||
									editExperienceMutation.isPending
								}
								size='sm'
								type='button'
								variant='tertiary'
							>
								<Settings01Icon />
								{t('vetting:merch.commons.customize_prompt')}
							</ButtonLegacy>
						}
					/>
				}
			>
				<div className='flex w-full flex-row gap-2 rounded-2xl border border-neutral-200'>
					<ExpShellMerchInputForm
						aiResponse={aiResponse}
						aiSubmissionRef={aiSubmissionRef}
						defaultValues={originalDefaultValues}
						handleAiSubmissionCb={handleAiSubmission}
						handleSaveCb={handleSaveOriginalMerchCopy}
						isReadOnly={false}
					/>
					<ExpShellMerchOutputForm
						defaultValues={
							improvedDefaultValues as ExperienceShellMerchOutputSchema
						}
						finalSubmitRef={finalSubmitRef}
						handleFinalSubmissionCb={() =>
							console.log('finalSubmissionCb')
						}
						handleSaveCb={handleSaveAiMerchCopy}
						isHostEdits={false}
						isLoading={isAiLoading}
						isReadOnly={false}
					/>
				</div>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={isBackLoading || isContinueLoading}
					onClick={() =>
						handleChangeStep({
							isBack: true,
							stepToGoTo: StepKeyName.enum.background
						})
					}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{isBackLoading && <Spinner />}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={isContinueLoading || isBackLoading}
					onClick={() =>
						handleChangeStep({
							stepToGoTo: StepKeyName.enum.location
						})
					}
					type='button'
				>
					{t('common:continue')}
					{isContinueLoading ? <Spinner /> : <ArrowRightIcon />}
				</ButtonLegacy>
			</Footer>
		</>
	)
}
